import { useCallback, useRef, useMemo } from 'react';
import { Button, Space, Tag, Modal, message, Alert, Switch } from 'antd';
import {
  DeleteOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  getFileShares,
  deleteFileShare,
  updateFileShare,
  type FileShareDocument,
  type FileShareFilters,
} from '@/api/fileApi';
import { useAuth } from '@/hooks/useAuth';
import FilesNav from './FilesNav';

export default function FileShareListPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const canRead = hasPermission('file', 'read');
  const canWrite = hasPermission('file', 'write');
  const canDelete = hasPermission('file', 'delete');

  const fetchData = useCallback(
    (params: SearchParams) => {
      const filters: FileShareFilters = { ...params };
      return getFileShares(filters);
    },
    []
  );

  const handleToggleActive = useCallback(
    async (id: string, isActive: boolean) => {
      try {
        await updateFileShare(id, { is_active: isActive });
        message.success(t('file.share_updated'));
        gridRef.current?.reload();
      } catch {
        message.error(t('app.error'));
      }
    },
    [t]
  );

  const handleCopyToken = useCallback(
    (token: string) => {
      navigator.clipboard.writeText(token);
      message.success(t('file.token_copied'));
    },
    [t]
  );

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('file.confirm_delete_share'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteFileShare(id);
            message.success(t('file.share_delete_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const columns: ColumnsType<FileShareDocument> = useMemo(
    () => [
      {
        title: t('file.field.file_id'),
        dataIndex: 'file_id',
        key: 'file_id',
        width: 200,
        ellipsis: true,
      },
      {
        title: t('file.field.shared_with_user'),
        dataIndex: 'shared_with_user_id',
        key: 'shared_with_user_id',
        width: 160,
        ellipsis: true,
        render: (v: string) => v || '-',
      },
      {
        title: t('file.field.shared_with_email'),
        dataIndex: 'shared_with_email',
        key: 'shared_with_email',
        width: 180,
        ellipsis: true,
        render: (v: string) => v || '-',
      },
      {
        title: t('file.field.permission'),
        dataIndex: 'permission',
        key: 'permission',
        width: 100,
        render: (v: string) => (
          <Tag color={v === 'download' ? 'green' : 'blue'}>
            {t(`file.permission_options.${v}`, v)}
          </Tag>
        ),
      },
      {
        title: t('file.field.is_active'),
        dataIndex: 'is_active',
        key: 'is_active',
        width: 80,
        render: (v: boolean, record: FileShareDocument) =>
          canWrite ? (
            <Switch
              checked={v}
              size="small"
              onChange={(checked) => handleToggleActive(record.id, checked)}
            />
          ) : (
            <Tag color={v ? 'green' : 'default'}>{v ? t('app.yes') : t('app.no')}</Tag>
          ),
      },
      {
        title: t('file.field.expires_at'),
        dataIndex: 'expires_at',
        key: 'expires_at',
        width: 160,
        render: (v: string) => {
          if (!v) return '-';
          const d = new Date(v);
          const isExpired = d < new Date();
          return (
            <span style={{ color: isExpired ? '#ff4d4f' : undefined }}>
              {d.toLocaleString()}
            </span>
          );
        },
      },
      {
        title: t('file.field.access_count'),
        dataIndex: 'access_count',
        key: 'access_count',
        width: 80,
      },
      {
        title: t('file.field.share_token'),
        dataIndex: 'share_token',
        key: 'share_token',
        width: 120,
        render: (v: string) =>
          v ? (
            <Space size="small">
              <span>{v.slice(0, 8)}...</span>
              <Button
                type="link"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => handleCopyToken(v)}
              />
            </Space>
          ) : (
            '-'
          ),
      },
      {
        title: t('file.field.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        width: 160,
        sorter: true,
        render: (v: string) => (v ? new Date(v).toLocaleString() : '-'),
      },
      {
        title: t('app.actions'),
        key: 'col_actions',
        width: 80,
        render: (_: unknown, record: FileShareDocument) => (
          <Space size="small">
            {canDelete && (
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
              />
            )}
          </Space>
        ),
      },
    ],
    [t, canWrite, canDelete, handleDelete, handleToggleActive, handleCopyToken]
  );

  if (!canRead) return <Alert type="error" message={t('app.forbidden')} />;

  return (
    <div>
      <FilesNav />
      <h2>{t('file.shares_title')}</h2>
      <DataGrid<FileShareDocument>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchable
        searchPlaceholder={t('file.search_shares_placeholder')}
        defaultSortField="created_at"
        defaultSortDescending
      />
    </div>
  );
}
